if (!customElements.get('sticky-product-bar')) {
  customElements.define(
    'sticky-product-bar',
    class StickyProductBar extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.section;
        this.productId = this.dataset.product;
        this.actionButton = this.querySelector('[data-sticky-action]');
        this.actionLabel = this.querySelector('[data-sticky-action-label]');
        this.optionsEl = this.querySelector('[data-sticky-options]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.mediaEl = this.querySelector('[data-sticky-media]');
        this.fallbackMediaSrc = this.mediaEl?.src || '';
        this.currentVariant = null;
        this.isBarVisible = false;
      }

      connectedCallback() {
        this.mainSection = document.getElementById(`MainProduct-${this.sectionId}`);
        this.productInfo = document.getElementById(`ProductInfo-${this.sectionId}`);
        this.variantPicker = this.mainSection?.querySelector(
          `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
        );
        this.productInputs = this.mainSection?.querySelector('.product__inputs');
        this.mainSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        if (!this.mainSection || !this.productInputs || !this.actionButton) return;

        this.seedVariantFromPicker();
        this.updateFromVariant(this.currentVariant);
        this.setupIntersectionObserver();
        this.setupVariantSubscription();
        this.variantPicker?.addEventListener('change', this.onPickerChange.bind(this));
        this.actionButton.addEventListener('click', this.onActionClick.bind(this));
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      onPickerChange() {
        this.seedVariantFromPicker();
        this.updateOptions();

        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
          if (!this.currentVariant.available) {
            this.updateActionButton(this.currentVariant);
          }
        } else {
          this.updateActionButton(null);
        }
      }

      seedVariantFromPicker() {
        if (this.variantPicker) {
          const variantData = JSON.parse(
            this.variantPicker.querySelector('[type="application/json"]').textContent
          );

          const selectedOptions = this.getSelectedOptions();
          this.currentVariant =
            variantData.find((variant) =>
              variant.options.every((option, index) => option === selectedOptions[index])
            ) || null;
          return;
        }

        const variantInput = document.querySelector(
          `#product-form-${this.sectionId}-${this.productId} input[name="id"]`
        );
        if (!variantInput?.value) return;

        this.currentVariant = {
          id: variantInput.value,
          available: !this.mainSubmitButton?.hasAttribute('disabled'),
        };
      }

      getSelectedOptions() {
        if (!this.variantPicker) return [];

        if (this.variantPicker.tagName === 'VARIANT-RADIOS') {
          return Array.from(this.variantPicker.querySelectorAll('fieldset')).map(
            (fieldset) => fieldset.querySelector('input:checked')?.value
          );
        }

        return Array.from(this.variantPicker.querySelectorAll('select')).map((select) => select.value);
      }

      setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.setBarVisible(!entry.isIntersecting);
          },
          { threshold: 0 }
        );

        this.intersectionObserver.observe(this.productInputs);
      }

      setupVariantSubscription() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          this.currentVariant = event.data.variant;
          this.updateFromVariant(this.currentVariant);
        });
      }

      setBarVisible(visible) {
        this.isBarVisible = visible;
        this.classList.toggle('is-visible', visible);
        this.toggleAttribute('hidden', !visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      }

      updateFromVariant(variant) {
        this.updateOptions();
        this.updatePrice();
        this.updateMedia(variant);
        this.updateActionButton(variant);
      }

      updateOptions() {
        if (!this.optionsEl) return;

        const options = this.getSelectedOptions().filter(Boolean);
        this.optionsEl.textContent = options.join(' / ');
        this.optionsEl.hidden = options.length === 0;
      }

      updatePrice() {
        if (!this.priceEl) return;

        const mainPrice = document.getElementById(`price-${this.sectionId}`);
        if (mainPrice) {
          this.priceEl.innerHTML = mainPrice.innerHTML;
        }
      }

      updateMedia(variant) {
        if (!this.mediaEl) return;

        const imageSrc = variant?.featured_image?.src || this.fallbackMediaSrc;
        if (imageSrc && this.mediaEl.src !== imageSrc) {
          this.mediaEl.src = imageSrc;
        }
      }

      updateActionButton(variant) {
        if (!this.actionButton || !this.actionLabel) return;

        const mainButtonDisabled = this.mainSubmitButton?.hasAttribute('disabled');
        const isAvailable = variant?.available && !mainButtonDisabled;

        if (isAvailable) {
          this.actionButton.dataset.action = 'add-to-cart';
          this.actionLabel.textContent = window.variantStrings.addToCart;
        } else {
          this.actionButton.dataset.action = 'select-options';
          this.actionLabel.textContent = window.variantStrings.selectOptions;
        }
      }

      onActionClick() {
        if (this.actionButton.dataset.action === 'add-to-cart') {
          this.mainSubmitButton?.click();
          this.actionButton.querySelector('.loader').classList.remove('hidden');
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source !== 'product-form') {
              return;
            }
            this.actionButton.querySelector('.loader').classList.add('hidden');
          });
          return;
        }

        (this.productInfo || this.productInputs)?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  );
}
